import { mergeAttributes, Node, nodeInputRule } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import PdfLinkComponent from './PdfLinkComponent';
import {
  identityTiptapFileSrcResolver,
  useTiptapFileSrc,
  type TiptapFileSrcResolver,
} from '../../../useTiptapFileSrc';

export interface PDFOptions {
  inline: boolean;
  HTMLAttributes: Record<string, any>;
  resolveFileSrc: TiptapFileSrcResolver;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    pdfLink: {
      /**
       * Add a PDF link
       */
      setPDF: (
        options: {
          href: string;
          title?: string;
          position?: number;
          fileDrn?: string;
        },
        position: number,
      ) => ReturnType;
    };
  }
}

export const inputRegex = /(?:^|\s)(\[(.+?)\]\((https?:\/\/.*?\.pdf)\))$/;

function PdfLinkTiptapWrapper(props) {
  const { node } = props;
  const href = useTiptapFileSrc(
    node.attrs.fileDrn,
    typeof node.attrs.href === 'string' ? node.attrs.href : '',
  );
  return (
    <NodeViewWrapper>
      <PdfLinkComponent href={href} title={node.attrs.title} />
    </NodeViewWrapper>
  );
}

export const Pdf = Node.create<PDFOptions>({
  name: 'pdf',

  addOptions() {
    return {
      inline: false,
      HTMLAttributes: {},
      resolveFileSrc: identityTiptapFileSrcResolver,
    };
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? 'inline' : 'block';
  },

  draggable: true,

  addAttributes() {
    return {
      href: {
        default: null,
      },
      title: {
        default: null,
      },
      fileDrn: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-file-drn'),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a[data-file-drn]',
        priority: 100,
      },
      {
        tag: 'a[href$=".pdf"]',
      },
    ];
  },

  renderHTML({ node }) {
    const { href, fileDrn, title } = node.attrs;
    const resolvedHref =
      typeof href === 'string' ? this.options.resolveFileSrc(fileDrn, href) : href;
    return [
      'a',
      mergeAttributes(this.options.HTMLAttributes, {
        href: resolvedHref,
        target: '_blank',
        rel: 'noopener noreferrer',
        ...(typeof fileDrn === 'string' ? { 'data-file-drn': fileDrn } : {}),
      }),
      `${title} ↪`,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer((props) => <PdfLinkTiptapWrapper {...props} />);
  },

  addCommands() {
    return {
      setPDF:
        (options, position) =>
        ({ chain }) => {
          return chain()
            .insertContentAt(position, {
              type: this.name,
              attrs: options,
            })
            .insertContentAt(position + 1, {
              type: 'paragraph',
              content: [],
            })
            .focus()
            .run();
        },
    };
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: (match) => {
          const [, , title, href] = match;
          return { href, title };
        },
      }),
    ];
  },
});
